import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  InputBase,
  Chip,
  CircularProgress,
  useMediaQuery,
  alpha,
  Grid,
  Drawer,
  Badge,
  Divider,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import useCachedQuery from '../hooks/useCachedQuery';
import {
  fetchProblemsList,
  fetchTopSolvers,
  queryKeys,
  STALE_TIMES,
} from '../api/queries';
import { useTheme } from '@emotion/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import { shuffleArray, formatProblemTitle } from '../common/utils';
import { MOCK_INTERVIEW_BOOKING_PRICE } from '../common/constants';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Helmet } from 'react-helmet';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import RouteIcon from '@mui/icons-material/Route';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ArchitectureOutlinedIcon from '@mui/icons-material/ArchitectureOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDispatch, useSelector } from 'react-redux';
import { showAuth } from '../redux/authSlice';
import { trophyColors } from './LeaderboardPage';
import UpcomingContestCountdown from '../components/UpcomingContestCountdown';
import { motion } from 'framer-motion';
import { INTERVIEW_TRACKS, countTopics } from '../data/interviewPrepCurriculum';
import MentorshipCallout from '../components/MentorshipCallout';
import IdeLanguageCallout from '../components/IdeLanguageCallout';
import TestimonialsSection from '../components/TestimonialsSection';
import { findContinueTarget } from '../common/practiceProgress';

const ACCENT = '#137274';
const PINK = '#f50057';

const SECTION_ORDER = [
  'Getting Started',
  'Patterns',
  'Number System',
  'Arrays',
  '2-D Array',
  'Strings',
  'Recursion & Backtracking',
  'Time & Space Complexity',
  'Stack and Queues',
  'Linked List',
  'Generic Trees',
  'Binary Trees',
  'Binary Search Trees',
  'HashMap & Heap',
  'Graph',
  'Dynamic Programming',
];

const DIFFICULTY_RANK = { easy: 1, medium: 2, hard: 3 };
const STATUS_FILTER_LABELS = {
  solved: 'Solved',
  unsolved: 'Unsolved',
  attempted: 'Attempted',
  bookmarked: 'Bookmarked',
  has_notes: 'Has notes',
};

const PREP_ICONS = {
  'system-design': ArchitectureOutlinedIcon,
  lld: AccountTreeOutlinedIcon,
  'cs-fundamentals': MemoryOutlinedIcon,
  behavioral: ForumOutlinedIcon,
};
const LIVE_PAIR_LABELS = ['Live Coding', 'Pair Programming'];

/** Soft-cycles between Live Coding and Pair Programming. */
const AlternatingLiveLabel = ({ sx = {}, intervalMs = 3200 }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % LIVE_PAIR_LABELS.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-grid',
        alignItems: 'center',
        justifyItems: 'start',
        ...sx,
      }}
    >
      {LIVE_PAIR_LABELS.map((label, i) => (
        <Box
          key={label}
          component={motion.span}
          initial={false}
          animate={{
            opacity: i === index ? 1 : 0,
            y: i === index ? 0 : 6,
          }}
          transition={{ duration: 0.35 }}
          sx={{
            gridArea: '1 / 1',
            whiteSpace: 'nowrap',
            pointerEvents: i === index ? 'auto' : 'none',
          }}
        >
          {label}
        </Box>
      ))}
    </Box>
  );
};

const pathways = [
  {
    title: 'Practice Track',
    body: 'Structured DSA path — level by level, day by day.',
    to: '/practice-track',
    icon: <RouteIcon sx={{ fontSize: 22 }} />,
    accent: ACCENT,
  },
  {
    title: 'Live Coding',
    alternateTitle: true,
    body: 'Pair program with shared editor, video, and run output.',
    to: '/live-coding',
    icon: <VideocamOutlinedIcon sx={{ fontSize: 22 }} />,
    accent: PINK,
  },
  {
    title: 'Contests',
    body: 'Timed rounds to test speed, accuracy, and nerves.',
    to: '/contests',
    icon: <BoltOutlinedIcon sx={{ fontSize: 22 }} />,
    accent: '#d97706',
  },
  {
    title: 'Online IDE',
    body: 'Run Python, Java, Go, Rust, TypeScript, and more — then share a link, free in your browser.',
    to: '/ide',
    icon: <CodeOutlinedIcon sx={{ fontSize: 22 }} />,
    accent: '#7c3aed',
  },
  {
    title: 'Mentorship',
    body: `1:1 with a FAANG SDE — full prep or a ₹${MOCK_INTERVIEW_BOOKING_PRICE} mock interview.`,
    to: '/mentorship',
    icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 22 }} />,
    accent: '#2563eb',
  },
];

const whyPoints = [
  {
    title: 'Practice with purpose',
    body: 'Curated problems, Practice Track, and topic filters so you never wonder what to solve next.',
  },
  {
    title: 'Compete & stay consistent',
    body: 'Contests, leaderboards, and the 75 Hard challenge keep momentum when motivation dips.',
  },
  {
    title: 'Collaborate like at work',
    body: 'Live Coding Room brings pair programming, interviews, and mentoring into one link.',
  },
];

const ProblemsPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [filteredProblems, setFilteredProblems] = useState([]);
  const [toppers, setToppers] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState(() => {
    const topic = searchParams.get('topic');
    return topic ? [topic] : [];
  });
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // all | unsolved | solved | attempted | bookmarked | has_notes
  const [sortBy, setSortBy] = useState('default'); // default | acceptance | difficulty | title
  const [uniqueTopics, setUniqueTopics] = useState([]);
  const [uniqueLevels, setUniqueLevels] = useState([]);
  const [uniqueSections, setUniqueSections] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const problemsRef = useRef(null);
  const problemsInitRef = useRef(false);

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #e8f4f5 0%, #f7f9fb 35%, #ffffff 100%)';

  const isHome = location.pathname === '/' || location.pathname === '';
  const topicParam = searchParams.get('topic');
  const showMarketing = isHome || (!topicParam && !searchInput);

  const difficulties = ['easy', 'medium', 'hard'];

  const toPascalCase = (str) =>
    String(str)
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

  const getAcceptanceRate = (problem) => {
    const ok = Number(problem?.successSubmission) || 0;
    const fail = Number(problem?.failedSubmission) || 0;
    const total = ok + fail;
    if (!total) return null;
    return Math.round((ok / total) * 100);
  };

  const toggleInList = (prev, value, equals = (a, b) => a === b) => {
    const exists = prev.some((item) => equals(item, value));
    return exists ? prev.filter((item) => !equals(item, value)) : [...prev, value];
  };

  const {
    data: problemsData,
    loading: problemsLoading,
    error: problemsError,
    refetch: refetchProblems,
  } = useCachedQuery(queryKeys.problems, fetchProblemsList, {
    staleTime: STALE_TIMES.problems,
  });
  const { data: toppersData } = useCachedQuery(
    queryKeys.topSolvers,
    fetchTopSolvers,
    { staleTime: STALE_TIMES.topSolvers },
  );

  useEffect(() => {
    if (!problemsData) return;

    setError(null);

    const buildFilterMeta = (list) => {
      const topicsSet = new Set();
      const levelsSet = new Set();
      const sectionsSet = new Set();
      list?.forEach((problem) => {
        (problem.topics || []).forEach((topic) => topicsSet.add(topic));
        if (problem.level != null && problem.level !== '') {
          levelsSet.add(String(problem.level));
        }
        if (problem.section) sectionsSet.add(problem.section);
      });
      const levels = Array.from(levelsSet).sort((a, b) => Number(a) - Number(b));
      const sections = Array.from(sectionsSet).sort((a, b) => {
        const ai = SECTION_ORDER.indexOf(a);
        const bi = SECTION_ORDER.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
      setUniqueTopics(Array.from(topicsSet).sort((a, b) => a.localeCompare(b)));
      setUniqueLevels(levels);
      setUniqueSections(sections);
    };

    if (!problemsInitRef.current) {
      problemsInitRef.current = true;
      const shuffledProblems = shuffleArray(problemsData);
      setProblems(shuffledProblems);
      setFilteredProblems(shuffledProblems);
      buildFilterMeta(shuffledProblems);
    } else {
      const bySlug = new Map(problemsData.map((p) => [p.slug, p]));
      const mergeList = (prev) =>
        prev.map((p) => ({ ...p, ...bySlug.get(p.slug) })).filter((p) => bySlug.has(p.slug));
      setProblems(mergeList);
      setFilteredProblems(mergeList);
      buildFilterMeta(problemsData);
    }

    setLoading(false);
  }, [problemsData]);

  useEffect(() => {
    if (problemsError) {
      setError('Error fetching problems');
      setLoading(false);
    }
  }, [problemsError]);

  useEffect(() => {
    if (problemsLoading && !problemsData) setLoading(true);
  }, [problemsLoading, problemsData]);

  const handleRetryProblems = () => {
    setError(null);
    setLoading(true);
    refetchProblems()
      .catch(() => setError('Error fetching problems'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (Array.isArray(toppersData)) setToppers(toppersData);
  }, [toppersData]);

  useEffect(() => {
    const topic = searchParams.get('topic');
    if (topic) setSelectedTopics([topic]);
  }, [searchParams]);

  useEffect(() => {
    const filtered = problems.filter((problem) => {
      const matchesSearch = (problem.title || '')
        .toLowerCase()
        .includes(searchInput.toLowerCase());
      const matchesTopic =
        selectedTopics.length === 0 ||
        selectedTopics.some((topic) =>
          (problem.topics || []).some(
            (t) => String(t).toLowerCase() === String(topic).toLowerCase(),
          ),
        );
      const matchesDifficulty =
        selectedDifficulties.length === 0 ||
        selectedDifficulties.includes(problem.difficulty);
      const matchesLevel =
        selectedLevels.length === 0 ||
        selectedLevels.includes(String(problem.level ?? ''));
      const matchesSection =
        selectedSections.length === 0 ||
        selectedSections.some(
          (section) =>
            String(problem.section || '').toLowerCase() === String(section).toLowerCase(),
        );
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'solved' && problem.isSolved) ||
        (statusFilter === 'unsolved' && !problem.isSolved) ||
        (statusFilter === 'attempted' && problem.isAttempted && !problem.isSolved) ||
        (statusFilter === 'bookmarked' && problem.isBookmarked) ||
        (statusFilter === 'has_notes' && problem.hasNote);
      return (
        matchesSearch &&
        matchesTopic &&
        matchesDifficulty &&
        matchesLevel &&
        matchesSection &&
        matchesStatus
      );
    });

    const sorted = [...filtered];
    if (sortBy === 'acceptance') {
      sorted.sort((a, b) => {
        const ar = getAcceptanceRate(a);
        const br = getAcceptanceRate(b);
        if (ar == null && br == null) return 0;
        if (ar == null) return 1;
        if (br == null) return -1;
        return br - ar;
      });
    } else if (sortBy === 'difficulty') {
      sorted.sort(
        (a, b) =>
          (DIFFICULTY_RANK[a.difficulty] || 99) - (DIFFICULTY_RANK[b.difficulty] || 99),
      );
    } else if (sortBy === 'title') {
      sorted.sort((a, b) =>
        formatProblemTitle(a.title).localeCompare(formatProblemTitle(b.title)),
      );
    }

    setFilteredProblems(sorted);
  }, [
    searchInput,
    selectedTopics,
    selectedDifficulties,
    selectedLevels,
    selectedSections,
    statusFilter,
    sortBy,
    problems,
  ]);

  const stats = useMemo(() => {
    const solved = problems.filter((p) => p.isSolved).length;
    return {
      total: problems.length,
      topics: uniqueTopics.length,
      solved,
      toppers: toppers.length,
    };
  }, [problems, uniqueTopics, toppers]);

  const continueTarget = useMemo(
    () => (isLoggedIn && !loading ? findContinueTarget(problems) : null),
    [isLoggedIn, loading, problems],
  );

  const hasActiveFilters =
    selectedTopics.length > 0 ||
    selectedDifficulties.length > 0 ||
    selectedLevels.length > 0 ||
    selectedSections.length > 0 ||
    statusFilter !== 'all' ||
    sortBy !== 'default';

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== 'all') count += 1;
    count += selectedDifficulties.length;
    count += selectedLevels.length;
    count += selectedSections.length;
    count += selectedTopics.length;
    if (sortBy !== 'default') count += 1;
    return count;
  }, [
    statusFilter,
    selectedDifficulties,
    selectedLevels,
    selectedSections,
    selectedTopics,
    sortBy,
  ]);

  const clearFilters = () => {
    setSelectedTopics([]);
    setSelectedDifficulties([]);
    setSelectedLevels([]);
    setSelectedSections([]);
    setStatusFilter('all');
    setSortBy('default');
  };

  const handleTopicClick = (topic) => {
    setSelectedTopics((prev) => {
      const next = toggleInList(
        prev,
        topic,
        (a, b) => String(a).toLowerCase() === String(b).toLowerCase(),
      );
      // Keep the URL in sync with the active topic filter (replace, not
      // push, so toggling tags doesn't spam browser history) — this is what
      // makes the topic chips real, crawlable links to /problems?topic=X
      // instead of JS-only filters Google never discovers.
      const nextUrl =
        next.length === 1 ? `/problems?topic=${encodeURIComponent(next[0])}` : '/problems';
      navigate(nextUrl, { replace: true });
      return next;
    });
  };

  const handleDifficultyClick = (difficulty) => {
    setSelectedDifficulties((prev) => toggleInList(prev, difficulty));
  };

  const handleLevelClick = (level) => {
    setSelectedLevels((prev) => toggleInList(prev, String(level)));
  };

  const handleSectionClick = (section) => {
    setSelectedSections((prev) =>
      toggleInList(
        prev,
        section,
        (a, b) => String(a).toLowerCase() === String(b).toLowerCase(),
      ),
    );
  };

  const chipSx = (active) => ({
    height: 20,
    fontWeight: 700,
    fontSize: '0.62rem',
    '& .MuiChip-label': { px: 0.65 },
    bgcolor: active ? ACCENT : 'transparent',
    color: active ? '#fff' : 'inherit',
    border: `1px solid ${active ? ACCENT : border}`,
  });

  const softChipSx = (active) => ({
    height: 18,
    fontWeight: 600,
    fontSize: '0.58rem',
    '& .MuiChip-label': { px: 0.55 },
    bgcolor: active ? alpha(ACCENT, isDark ? 0.25 : 0.12) : 'transparent',
    color: active ? ACCENT : muted,
    border: `1px solid ${active ? alpha(ACCENT, 0.4) : border}`,
  });

  const filterSection = (title, children) => (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', mb: 0.75, color: muted }}>
        {title}
      </Typography>
      {children}
    </Box>
  );

  const filtersPanel = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {filterSection(
        'Status',
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'unsolved', label: 'Unsolved' },
            { id: 'solved', label: 'Solved' },
            { id: 'attempted', label: 'Attempted' },
            { id: 'bookmarked', label: 'Bookmarked' },
            { id: 'has_notes', label: 'Has notes' },
          ].map((item) => (
            <Chip
              key={item.id}
              label={item.label}
              clickable
              size="small"
              onClick={() => setStatusFilter(item.id)}
              sx={chipSx(statusFilter === item.id)}
            />
          ))}
        </Box>,
      )}

      {filterSection(
        'Difficulty',
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {difficulties.map((difficulty) => {
            const active = selectedDifficulties.includes(difficulty);
            return (
              <Chip
                key={difficulty}
                label={toPascalCase(difficulty)}
                clickable
                onClick={() => handleDifficultyClick(difficulty)}
                size="small"
                sx={chipSx(active)}
              />
            );
          })}
        </Box>,
      )}

      {uniqueLevels.length > 0 &&
        filterSection(
          'Level',
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {uniqueLevels.map((level) => {
              const active = selectedLevels.includes(String(level));
              return (
                <Chip
                  key={level}
                  label={`Level ${level}`}
                  clickable
                  size="small"
                  onClick={() => handleLevelClick(level)}
                  sx={chipSx(active)}
                />
              );
            })}
          </Box>,
        )}

      {uniqueSections.length > 0 &&
        filterSection(
          `Track topics (${uniqueSections.length})`,
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
            {uniqueSections.map((section) => {
              const active = selectedSections.some(
                (s) => String(s).toLowerCase() === String(section).toLowerCase(),
              );
              return (
                <Chip
                  key={section}
                  label={section}
                  clickable
                  size="small"
                  onClick={() => handleSectionClick(section)}
                  sx={softChipSx(active)}
                />
              );
            })}
          </Box>,
        )}

      {filterSection(
        `Tags (${uniqueTopics.length})`,
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
          {uniqueTopics.map((topic) => {
            const active = selectedTopics.some(
              (t) => String(t).toLowerCase() === String(topic).toLowerCase(),
            );
            return (
              <Chip
                key={topic}
                component={Link}
                to={`/problems?topic=${encodeURIComponent(topic)}`}
                label={toPascalCase(topic)}
                clickable
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  handleTopicClick(topic);
                }}
                sx={softChipSx(active)}
              />
            );
          })}
        </Box>,
      )}

      {filterSection(
        'Sort by',
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {[
            { id: 'default', label: 'Default' },
            { id: 'acceptance', label: 'Acceptance' },
            { id: 'difficulty', label: 'Difficulty' },
            { id: 'title', label: 'A–Z' },
          ].map((item) => (
            <Chip
              key={item.id}
              label={item.label}
              clickable
              size="small"
              onClick={() => setSortBy(item.id)}
              sx={chipSx(sortBy === item.id)}
            />
          ))}
        </Box>,
      )}
    </Box>
  );

  const handlePickForMe = () => {
    const unsolvedProblems = filteredProblems.filter((problem) => !problem.isSolved);
    if (unsolvedProblems.length > 0) {
      const randomProblem =
        unsolvedProblems[Math.floor(Math.random() * unsolvedProblems.length)];
      navigate(`/problems/${randomProblem.slug}`);
    } else {
      alert('You do not have any problem left to solve!');
    }
  };

  const scrollToProblems = () => {
    problemsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Header "Problem Set" → /problems#problemset scrolls to the explorer
  useEffect(() => {
    if (location.hash !== '#problemset') return undefined;
    const timer = window.setTimeout(() => {
      problemsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [location.hash, location.pathname]);

  const handleStartPractice = () => {
    if (!isLoggedIn) {
      dispatch(showAuth());
      return;
    }
    navigate('/practice-track');
  };

  return (
    <Box sx={{ background: pageBg, minHeight: '100vh' }}>
      <Helmet>
        <title>
          {topicParam
            ? `${toPascalCase(topicParam)} Problems | Practice DSA | CrafterHack`
            : isHome
              ? 'CrafterHack | Practice, Contests & Live Coding'
              : 'Coding problems | CrafterHack'}
        </title>
        <meta
          name="description"
          content={
            topicParam
              ? `Practice ${toPascalCase(topicParam)} coding problems with test cases and an online IDE on CrafterHack.`
              : 'Practice DSA, join contests, pair program live, and prepare for interviews on CrafterHack.'
          }
        />
        <link
          rel="canonical"
          href={
            topicParam
              ? `https://crafterhack.com/problems?topic=${encodeURIComponent(topicParam)}`
              : isHome
                ? 'https://crafterhack.com/'
                : 'https://crafterhack.com/problems'
          }
        />
      </Helmet>

      {/* HERO */}
      {showMarketing && (
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            px: { xs: 2, sm: 3 },
            pt: { xs: 4, md: 6 },
            pb: { xs: 4, md: 5 },
            borderBottom: `1px solid ${border}`,
            background: isDark
              ? `radial-gradient(1200px 500px at 10% -10%, ${alpha(ACCENT, 0.35)}, transparent 55%), radial-gradient(900px 400px at 90% 0%, ${alpha(PINK, 0.18)}, transparent 50%), #0f1419`
              : `radial-gradient(1100px 480px at 8% -20%, ${alpha(ACCENT, 0.22)}, transparent 55%), radial-gradient(800px 380px at 95% 0%, ${alpha(PINK, 0.1)}, transparent 50%), linear-gradient(180deg, #eef7f8 0%, #f8fafb 100%)`,
          }}
        >
          <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              <Typography
                component="div"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2rem', sm: '2.6rem', md: '3.1rem' },
                  letterSpacing: '-0.045em',
                  lineHeight: 1.05,
                  mb: 1.5,
                  color: isDark ? '#e6edf3' : '#0b2426',
                }}
              >
                Crafter
                <Box component="span" sx={{ color: PINK }}>
                  Hack
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.15rem', sm: '1.35rem' },
                  letterSpacing: '-0.02em',
                  mb: 1.25,
                  maxWidth: 560,
                  color: isDark ? '#c9d1d9' : '#1a2b2c',
                }}
              >
                Practice hard. Compete live. Interview ready.
              </Typography>

              <Typography
                sx={{
                  color: muted,
                  maxWidth: 520,
                  mb: 3,
                  fontSize: { xs: '0.95rem', sm: '1.02rem' },
                  lineHeight: 1.55,
                }}
              >
                One platform for DSA practice, contests, pair programming, and mock interviews —
                built for learners who ship consistency.
              </Typography>

              <Box
                data-tour="hero-cta"
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 3.5 }}
              >
                {continueTarget ? (
                  <Button
                    component={Link}
                    to={`/problems/${continueTarget.slug}`}
                    variant="contained"
                    size="large"
                    startIcon={<PlayArrowRoundedIcon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      px: 2.75,
                      py: 1.15,
                      borderRadius: 2,
                      bgcolor: ACCENT,
                      boxShadow: `0 10px 24px ${alpha(ACCENT, 0.3)}`,
                      '&:hover': { bgcolor: '#0f5c5e' },
                    }}
                  >
                    Continue · {formatProblemTitle(continueTarget.title)}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleStartPractice}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      px: 2.75,
                      py: 1.15,
                      borderRadius: 2,
                      bgcolor: ACCENT,
                      boxShadow: `0 10px 24px ${alpha(ACCENT, 0.3)}`,
                      '&:hover': { bgcolor: '#0f5c5e' },
                    }}
                  >
                    Start Practice Track
                  </Button>
                )}
                {continueTarget && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleStartPractice}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 2.5,
                      borderRadius: 2,
                      borderColor: border,
                      color: isDark ? '#e6edf3' : '#0f2a2b',
                      bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.55),
                    }}
                  >
                    Practice Track
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="large"
                  onClick={scrollToProblems}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 2.5,
                    borderRadius: 2,
                    borderColor: border,
                    color: isDark ? '#e6edf3' : '#0f2a2b',
                    bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.55),
                  }}
                >
                  Browse problems
                </Button>
                <Button
                  variant="text"
                  size="large"
                  startIcon={<VideocamOutlinedIcon />}
                  onClick={() => navigate('/live-coding')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: PINK,
                    minWidth: 168,
                    justifyContent: 'flex-start',
                  }}
                >
                  <AlternatingLiveLabel />
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                {[
                  { label: 'Problems', value: loading ? '—' : stats.total },
                  { label: 'Topics', value: loading ? '—' : stats.topics },
                  { label: 'Top solvers', value: toppers.length || '—' },
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      px: 1.75,
                      py: 1,
                      borderRadius: 2,
                      border: `1px solid ${border}`,
                      bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.7),
                      minWidth: 100,
                    }}
                  >
                    <Typography sx={{ fontWeight: 900, fontSize: '1.2rem', letterSpacing: '-0.03em' }}>
                      {s.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: muted, fontWeight: 600 }}>
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Box>
        </Box>
      )}

      <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, md: 4 } }}>
        {/* Pathways */}
        {showMarketing && (
          <Box sx={{ mb: 4 }} data-tour="pathways">
            <Typography
              sx={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.02em', mb: 0.5 }}
            >
              Where do you want to go?
            </Typography>
            <Typography variant="body2" sx={{ color: muted, mb: 2 }}>
              Pick a path — everything connects back to becoming interview-ready.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(5, minmax(0, 1fr))',
                },
                gap: 1.5,
              }}
            >
              {pathways.map((p, i) => (
                <motion.div
                  key={p.to}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                  style={{ height: '100%' }}
                >
                  <Box
                    component={Link}
                    to={p.to}
                    sx={{
                      display: 'block',
                      height: '100%',
                      textDecoration: 'none',
                      color: 'inherit',
                      p: { xs: 2, md: 1.5 },
                      borderRadius: 2.5,
                      border: `1px solid ${border}`,
                      bgcolor: surface,
                      transition: 'border-color 0.2s, transform 0.2s',
                      '&:hover': {
                        borderColor: alpha(p.accent, 0.5),
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.25,
                        bgcolor: alpha(p.accent, isDark ? 0.2 : 0.12),
                        color: p.accent,
                      }}
                    >
                      {p.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '0.95rem', md: '0.88rem' }, minHeight: '1.4em' }}>
                      {p.alternateTitle ? <AlternatingLiveLabel /> : p.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: muted, fontSize: { xs: '0.8rem', md: '0.75rem' }, lineHeight: 1.45 }}>
                      {p.body}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Box>
        )}

        {showMarketing && (
          <Box sx={{ mb: 4 }}>
            <MentorshipCallout variant="banner" />
          </Box>
        )}

        {/* Interview prep: System Design, LLD, CS, Behavioral */}
        {showMarketing && (
          <Box sx={{ mb: 4 }} data-tour="interview-prep">
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'flex-end' },
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ maxWidth: 560 }}>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: ACCENT,
                    mb: 0.5,
                  }}
                >
                  Beyond DSA
                </Typography>
                <Typography
                  sx={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.02em', mb: 0.5 }}
                >
                  System Design, LLD & interview fundamentals
                </Typography>
                <Typography variant="body2" sx={{ color: muted }}>
                  The same topics senior loops probe — HLD, object-oriented design, CS foundations,
                  production backend, and behavioral stories.
                </Typography>
              </Box>
              <Button
                component={Link}
                to="/interview-prep"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  color: ACCENT,
                  flexShrink: 0,
                }}
              >
                View full curriculum
              </Button>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
                gap: 1.5,
              }}
            >
              {INTERVIEW_TRACKS.map((track, i) => {
                const Icon = PREP_ICONS[track.id] || ArchitectureOutlinedIcon;
                return (
                  <motion.div
                    key={track.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.04 * i, duration: 0.3 }}
                  >
                    <Box
                      component={Link}
                      to={`/interview-prep/${track.id}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        textDecoration: 'none',
                        color: 'inherit',
                        p: 2,
                        borderRadius: 2.5,
                        border: `1px solid ${border}`,
                        bgcolor: surface,
                        transition: 'border-color 0.2s, transform 0.2s',
                        '&:hover': {
                          borderColor: alpha(track.accent, 0.5),
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1.25,
                          bgcolor: alpha(track.accent, isDark ? 0.2 : 0.12),
                          color: track.accent,
                        }}
                      >
                        <Icon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', mb: 0.35 }}>
                        {track.shortTitle}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: muted, fontSize: '0.78rem', lineHeight: 1.45, flex: 1, mb: 1.25 }}
                      >
                        {track.homeBlurb}
                      </Typography>
                      {track.platforms?.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          {track.platforms.slice(0, 4).map((p) => (
                            <Chip
                              key={p}
                              label={p}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                bgcolor: 'transparent',
                                border: `1px solid ${border}`,
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          color: muted,
                        }}
                      >
                        {countTopics(track)} topics · Explore →
                      </Typography>
                    </Box>
                  </motion.div>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Spotlight: 75 Hard + Contest */}
        <Box
          data-tour="spotlight"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 4,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.75,
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              background: isDark
                ? `linear-gradient(135deg, ${alpha('#ff9800', 0.12)}, ${surface})`
                : `linear-gradient(135deg, ${alpha('#ff9800', 0.1)}, #fff)`,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#ff9800', 0.18),
                flexShrink: 0,
              }}
            >
              <EmojiEventsIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate('/challenges/75-hard-coding-challenge')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                  75 Hard Coding Challenge
                </Typography>
                <Chip
                  label="Live"
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#1a7f37', color: '#fff' }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: muted, fontSize: '0.8rem' }}>
                One problem a day for 75 days. Build an unbreakable streak.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/challenges/75-hard-coding-challenge')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                flexShrink: 0,
                bgcolor: ACCENT,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#0f5c5e' },
              }}
            >
              Start now
            </Button>
          </Paper>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignSelf: 'stretch',
              '& > *': { flex: 1 },
            }}
          >
            <UpcomingContestCountdown compact />
          </Box>
        </Box>

        {/* Problems explorer */}
        {/* Problems explorer — sticky filters/search; only list scrolls */}
        <Box id="problemset" ref={problemsRef} sx={{ scrollMarginTop: 80 }}>
          {continueTarget && (
            <Box
              sx={{
                mb: 2,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2.5,
                border: `1px solid ${alpha(ACCENT, 0.35)}`,
                bgcolor: isDark ? alpha(ACCENT, 0.12) : alpha(ACCENT, 0.06),
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 0.25 }}>
                  Pick up where you left off
                </Typography>
                <Typography variant="body2" sx={{ color: muted }}>
                  {continueTarget.section
                    ? `${continueTarget.section} · ${formatProblemTitle(continueTarget.title)}`
                    : formatProblemTitle(continueTarget.title)}
                </Typography>
              </Box>
              <Button
                component={Link}
                to={`/problems/${continueTarget.slug}`}
                variant="contained"
                startIcon={<PlayArrowRoundedIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: ACCENT,
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': { bgcolor: '#0f5c5e' },
                }}
              >
                Continue solving
              </Button>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'flex-end' },
              justifyContent: 'space-between',
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.02em' }}>
                Problem Set
              </Typography>
              <Typography variant="body2" sx={{ color: muted }}>
                {loading
                  ? 'Loading…'
                  : `${filteredProblems.length} problem${filteredProblems.length === 1 ? '' : 's'} match your filters`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
              {!isHome && (
                <Button
                  size="small"
                  onClick={() => window.dispatchEvent(new CustomEvent('ch-start-problems-tour'))}
                  sx={{ textTransform: 'none', fontWeight: 700, color: muted }}
                >
                  Tour
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={!isMobile ? <LeaderboardIcon /> : undefined}
                onClick={() => navigate('/leaderboard')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: border,
                  color: 'inherit',
                  flex: { xs: 1, sm: 'none' },
                }}
              >
                {isMobile ? 'Ranks' : 'Leaderboard'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={!isMobile ? <CodeOutlinedIcon /> : undefined}
                onClick={() => navigate('/ide')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: border,
                  color: 'inherit',
                  flex: { xs: 1, sm: 'none' },
                }}
              >
                IDE
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: { md: 'calc(100dvh - 110px)' },
              minHeight: { md: 480 },
              maxHeight: { md: 'calc(100dvh - 110px)' },
            }}
          >
            {!isMobile && toppers.length > 0 && (
              <Box
                sx={{
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 0.5 }}>
                  <GroupsOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '0.8rem' }}>Top solvers</Typography>
                </Box>
                {toppers.slice(0, 3).map((solver, index) => (
                  <Box
                    key={solver.username || solver.name}
                    onClick={() => navigate(`/user/${solver.username}`)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: isDark ? alpha('#fff', 0.05) : alpha(ACCENT, 0.06) },
                    }}
                  >
                    <EmojiEventsIcon sx={{ color: trophyColors[index], fontSize: 18 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{solver.name}</Typography>
                    <Typography variant="caption" sx={{ color: muted }}>
                      {solver.totalSolvedProblems}
                    </Typography>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() => navigate('/leaderboard')}
                  sx={{ ml: 'auto', textTransform: 'none', fontWeight: 700, color: ACCENT }}
                >
                  View ranks
                </Button>
              </Box>
            )}

            <Box
              data-tour="filters"
              sx={{
                flex: 1,
                minWidth: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: { md: '100%' },
              }}
            >
              <Paper
                elevation={0}
                component="form"
                data-tour="problem-search"
                onSubmit={(e) => e.preventDefault()}
                sx={{
                  mb: 1,
                  p: 0.5,
                  pl: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: 2.5,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  flexShrink: 0,
                }}
              >
                <SearchIcon sx={{ color: muted, fontSize: 20 }} />
                <InputBase
                  sx={{ flex: 1, fontSize: '0.9rem' }}
                  placeholder="Search problems…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <Badge
                  badgeContent={activeFilterCount}
                  color="primary"
                  invisible={activeFilterCount === 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      bgcolor: ACCENT,
                      fontWeight: 800,
                      fontSize: '0.65rem',
                      minWidth: 16,
                      height: 16,
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setFiltersOpen(true)}
                    aria-label="Filter problems"
                    sx={{
                      color: hasActiveFilters ? ACCENT : muted,
                      bgcolor: hasActiveFilters ? alpha(ACCENT, isDark ? 0.18 : 0.1) : 'transparent',
                      border: `1px solid ${hasActiveFilters ? alpha(ACCENT, 0.35) : border}`,
                      borderRadius: 1.5,
                      mx: 0.25,
                    }}
                  >
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </Badge>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handlePickForMe}
                  startIcon={<ShuffleIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: ACCENT,
                    mr: 0.5,
                    '&:hover': { bgcolor: '#0f5c5e' },
                  }}
                >
                  Random
                </Button>
              </Paper>

              {hasActiveFilters && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, alignItems: 'center' }}>
                  {statusFilter !== 'all' && (
                    <Chip
                      size="small"
                      label={STATUS_FILTER_LABELS[statusFilter] || statusFilter}
                      onDelete={() => setStatusFilter('all')}
                      sx={chipSx(true)}
                    />
                  )}
                  {selectedDifficulties.map((d) => (
                    <Chip
                      key={d}
                      size="small"
                      label={toPascalCase(d)}
                      onDelete={() => handleDifficultyClick(d)}
                      sx={chipSx(true)}
                    />
                  ))}
                  {selectedLevels.map((level) => (
                    <Chip
                      key={level}
                      size="small"
                      label={`Level ${level}`}
                      onDelete={() => handleLevelClick(level)}
                      sx={chipSx(true)}
                    />
                  ))}
                  {selectedSections.map((section) => (
                    <Chip
                      key={section}
                      size="small"
                      label={section}
                      onDelete={() => handleSectionClick(section)}
                      sx={{ ...softChipSx(true), height: 22, fontSize: '0.62rem' }}
                    />
                  ))}
                  {selectedTopics.map((topic) => (
                    <Chip
                      key={topic}
                      size="small"
                      label={toPascalCase(topic)}
                      onDelete={() => handleTopicClick(topic)}
                      sx={{ ...softChipSx(true), height: 22, fontSize: '0.62rem' }}
                    />
                  ))}
                  {sortBy !== 'default' && (
                    <Chip
                      size="small"
                      label={`Sort: ${
                        sortBy === 'acceptance'
                          ? 'Acceptance'
                          : sortBy === 'difficulty'
                            ? 'Difficulty'
                            : 'A–Z'
                      }`}
                      onDelete={() => setSortBy('default')}
                      sx={chipSx(true)}
                    />
                  )}
                  <Button
                    size="small"
                    onClick={clearFilters}
                    sx={{ textTransform: 'none', fontWeight: 700, color: muted, minWidth: 'auto', px: 0.5 }}
                  >
                    Clear all
                  </Button>
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{ color: muted, mb: 1, display: 'block', fontWeight: 600 }}
              >
                Showing {filteredProblems.length} of {problems.length}
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: ACCENT }} />
                </Box>
              ) : error && problems.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    px: 2,
                    borderRadius: 2.5,
                    border: `1px dashed ${border}`,
                    bgcolor: surface,
                  }}
                >
                  <WifiOffOutlinedIcon sx={{ fontSize: 28, color: muted, mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                    Couldn&apos;t load problems
                  </Typography>
                  <Typography variant="body2" sx={{ color: muted, mb: 2 }}>
                    Something went wrong on our end. Give it another try.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleRetryProblems}
                    startIcon={<RefreshIcon fontSize="small" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: ACCENT,
                      color: ACCENT,
                      '&:hover': { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.06) },
                    }}
                  >
                    Try again
                  </Button>
                </Box>
              ) : filteredProblems.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    borderRadius: 2.5,
                    border: `1px dashed ${border}`,
                    bgcolor: surface,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No problems found</Typography>
                  <Typography variant="body2" sx={{ color: muted }}>
                    Try clearing filters or searching a different keyword.
                  </Typography>
                </Box>
              ) : (
                <Box
                  data-tour="problem-list"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.55,
                    flex: 1,
                    minHeight: 0,
                    overflowY: { xs: 'visible', md: 'auto' },
                    overscrollBehavior: 'contain',
                    pr: { md: 0.5 },
                    pb: 0.75,
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { width: 8 },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: isDark ? '#30363d' : '#c5d0d3',
                      borderRadius: 8,
                    },
                  }}
                >
                  {filteredProblems.map((problem) => {
                    const acceptance = getAcceptanceRate(problem);
                    return (
                    <Box
                      key={problem._id}
                      component={Link}
                      to={`/problems/${problem.slug}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.25,
                        py: 0.7,
                        textDecoration: 'none',
                        color: 'inherit',
                        borderRadius: 1.5,
                        border: `1px solid ${border}`,
                        bgcolor: surface,
                        transition: 'border-color 0.15s, background 0.15s',
                        '&:hover': {
                          borderColor: alpha(ACCENT, 0.45),
                          bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.04),
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 650,
                            fontSize: '0.82rem',
                            lineHeight: 1.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatProblemTitle(problem.title)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: muted, fontSize: '0.62rem', lineHeight: 1.15 }}
                        >
                          {problem.topics?.[0]
                            ? `${toPascalCase(problem.topics[0])}${
                                problem.topics.length > 1
                                  ? ` +${problem.topics.length - 1}`
                                  : ''
                              }`
                            : 'General'}
                          {acceptance != null ? ` · ${acceptance}% accepted` : ''}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          flexShrink: 0,
                          width: 42,
                          textAlign: 'right',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          color:
                            acceptance == null
                              ? muted
                              : acceptance >= 50
                                ? '#1a7f37'
                                : acceptance >= 30
                                  ? '#b45309'
                                  : '#cf222e',
                        }}
                      >
                        {acceptance == null ? '—' : `${acceptance}%`}
                      </Typography>

                      <Chip
                        size="small"
                        label={
                          problem.difficulty
                            ? problem.difficulty.charAt(0).toUpperCase() +
                              problem.difficulty.slice(1)
                            : 'N/A'
                        }
                        sx={{
                          height: 20,
                          fontWeight: 700,
                          fontSize: '0.6rem',
                          flexShrink: 0,
                          '& .MuiChip-label': { px: 0.7 },
                          bgcolor:
                            problem.difficulty === 'easy'
                              ? alpha('#1a7f37', isDark ? 0.2 : 0.1)
                              : problem.difficulty === 'medium'
                                ? alpha('#d97706', isDark ? 0.2 : 0.1)
                                : alpha('#cf222e', isDark ? 0.2 : 0.1),
                          color:
                            problem.difficulty === 'easy'
                              ? '#1a7f37'
                              : problem.difficulty === 'medium'
                                ? '#b45309'
                                : '#cf222e',
                        }}
                      />
                      {problem.isSolved ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#1a7f37', flexShrink: 0 }} />
                      ) : (
                        problem.isAttempted && (
                          <Tooltip title="Attempted, not yet solved">
                            <PendingOutlinedIcon
                              sx={{ fontSize: 16, color: '#d97706', flexShrink: 0 }}
                            />
                          </Tooltip>
                        )
                      )}
                      {problem.isBookmarked && (
                        <BookmarkRoundedIcon sx={{ fontSize: 16, color: ACCENT, flexShrink: 0 }} />
                      )}
                    </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {showMarketing && (
          <Box sx={{ mt: 4, mb: 4 }}>
            <IdeLanguageCallout />
          </Box>
        )}

        <Drawer
          anchor={isMobile ? 'bottom' : 'right'}
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 380 },
              maxHeight: { xs: '88vh', sm: '100%' },
              borderTopLeftRadius: isMobile ? 16 : 0,
              borderTopRightRadius: isMobile ? 16 : 0,
              bgcolor: surface,
              borderLeft: isMobile ? 'none' : `1px solid ${border}`,
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
                flexShrink: 0,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', letterSpacing: '-0.02em' }}>
                  Filters
                </Typography>
                <Typography variant="caption" sx={{ color: muted }}>
                  Status, difficulty, level, topics & sort
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setFiltersOpen(false)} aria-label="Close filters">
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2, flexShrink: 0 }} />
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pr: 0.5 }}>{filtersPanel}</Box>
            <Box
              sx={{
                pt: 2,
                mt: 'auto',
                display: 'flex',
                gap: 1,
                flexShrink: 0,
                borderTop: `1px solid ${border}`,
              }}
            >
              {hasActiveFilters && (
                <Button
                  onClick={clearFilters}
                  sx={{ textTransform: 'none', fontWeight: 700, color: muted, flexShrink: 0 }}
                >
                  Clear
                </Button>
              )}
              <Button
                fullWidth
                variant="contained"
                onClick={() => setFiltersOpen(false)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: ACCENT,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#0f5c5e' },
                }}
              >
                Show {filteredProblems.length} problem{filteredProblems.length === 1 ? '' : 's'}
              </Button>
            </Box>
          </Box>
        </Drawer>

        {/* Why / How — below problem set */}
        {showMarketing && (
          <Box sx={{ mt: 4, mb: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.02em', mb: 2 }}>
              Why CrafterHack
            </Typography>
            <Grid container spacing={1.5}>
              {whyPoints.map((w) => (
                <Grid item xs={12} md={4} key={w.title}>
                  <Box
                    sx={{
                      p: 2.25,
                      height: '100%',
                      borderRadius: 2.5,
                      border: `1px solid ${border}`,
                      bgcolor: surface,
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, mb: 0.75 }}>{w.title}</Typography>
                    <Typography variant="body2" sx={{ color: muted, lineHeight: 1.5, fontSize: '0.85rem' }}>
                      {w.body}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {showMarketing && (
          <TestimonialsSection
            context="all"
            title="What engineers say"
            subtitle="From 1:1 mentorship and mocks to daily practice — engineers use CrafterHack to get interview-ready."
            sx={{ mt: 5 }}
          />
        )}

        {/* Closing CTA */}
        {showMarketing && (
          <Box
            sx={{
              mt: 5,
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: 3,
              border: `1px solid ${border}`,
              bgcolor: surface,
              background: isDark
                ? `linear-gradient(120deg, ${alpha(ACCENT, 0.2)}, ${alpha(PINK, 0.1)})`
                : `linear-gradient(120deg, ${alpha(ACCENT, 0.1)}, ${alpha(PINK, 0.06)})`,
              textAlign: { xs: 'left', sm: 'center' },
            }}
          >
            <FitnessCenterIcon sx={{ color: ACCENT, fontSize: 36, mb: 1 }} />
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                letterSpacing: '-0.03em',
                mb: 0.75,
              }}
            >
              Ready for your next breakthrough?
            </Typography>
            <Typography variant="body2" sx={{ color: muted, mb: 2.5, maxWidth: 480, mx: { sm: 'auto' } }}>
              Join Practice Track, start 1:1 mentorship with a FAANG SDE, or open a Live Coding Room.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', justifyContent: { sm: 'center' } }}>
              <Button
                variant="contained"
                onClick={handleStartPractice}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: ACCENT,
                  '&:hover': { bgcolor: '#0f5c5e' },
                }}
              >
                Open Practice Track
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/mentorship')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: border,
                  color: 'inherit',
                }}
              >
                Explore mentorship
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProblemsPage;
